/**
 * The page a user-blocking failure happened on, as a link that carries no
 * user content and no credential (#6012). Only route names on an allowlist
 * and `ot_` task refs inside a workspace survive; every other segment
 * (invite and share tokens, drive paths, file names, ids) becomes `_`.
 */

/** Static routes under `/<orgSlug>/` (src/app/[orgSlug]). */
const WORKSPACE_ROUTES = new Set([
  'activity',
  'aesthetics',
  'apps',
  'assets',
  'avatars',
  'bash',
  'branding',
  'chats',
  'connectors',
  'dashboard',
  'design',
  'documents',
  'downloads',
  'drives',
  'edit',
  'explore',
  'extensions',
  'files',
  'imai',
  'keel-alert-probe',
  'library',
  'products',
  'projects',
  'references',
  'scheduled',
  'settings',
  'studio',
  'support',
  'tech-prints',
  'techstudio',
  'thread-assets',
  'threads',
  'trash',
  'trend-analysis',
]);

/** Static top-level routes (src/app), which are never a workspace. */
const TOP_LEVEL_ROUTES = new Set([
  'admin',
  'agent-payments',
  'auth',
  'connect',
  'connectors',
  'dashboard',
  'embed',
  'invite',
  'login',
  'onboarding',
  'org-setup',
  'pricing',
  'privacy',
  'settings',
  'share',
  'shared',
  'shopify',
  'signup',
  'terms',
]);

/** Static names below a top-level route, e.g. `/share/threads/<token>`. */
const NESTED_STATIC_ROUTES = new Set([
  'apps',
  'artifacts',
  'desktop',
  'files',
  'guest-apps',
  'sign-out',
  'signing-in',
  'threads',
]);

const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,79}$/;
const TASK_REF_PATTERN = /^ot_[A-Za-z0-9_-]{4,64}$/;
/** A UUID or a long opaque string reads as a credential, even as a slug. */
const TOKEN_LIKE_PATTERN =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|.{32,})$/i;
const MAX_SEGMENTS = 6;

export interface RedactedPagePath {
  /** `/lune-atelier/threads/ot_x`, `/invite/_`; undefined off a page. */
  path?: string;
  /** The workspace the path is in, when it is a workspace route. */
  workspaceSlug?: string;
  /** Whether no segment was blanked, so the path still opens that page. */
  linkable: boolean;
}

export function redactUserBlockingPagePath(
  pathname: string | null | undefined,
  /** The workspace the boundary admitted, which alone vouches for `/<slug>`. */
  knownWorkspaceSlug?: string | null
): RedactedPagePath {
  if (typeof pathname !== 'string' || !pathname.startsWith('/'))
    return { linkable: false };
  const segments = pathname.split('/').filter(Boolean).slice(0, MAX_SEGMENTS);
  const [first, second] = segments;
  if (!first) return { linkable: false };
  // `/contact`, `/de` and other single-segment pages are not workspaces;
  // a workspace home counts only when the boundary admitted that workspace.
  const workspaceSlug =
    !TOP_LEVEL_ROUTES.has(first) &&
    SLUG_PATTERN.test(first) &&
    !TOKEN_LIKE_PATTERN.test(first) &&
    (second === undefined
      ? first === knownWorkspaceSlug
      : WORKSPACE_ROUTES.has(second))
      ? first
      : undefined;
  const kept = segments.map((segment, index) => {
    if (index === 0)
      return workspaceSlug || TOP_LEVEL_ROUTES.has(segment) ? segment : '_';
    if (workspaceSlug) {
      if (index === 1) return WORKSPACE_ROUTES.has(segment) ? segment : '_';
      // Only a task page's ref; a folder named `ot_…` is still user content.
      return index === 2 &&
        second === 'threads' &&
        TASK_REF_PATTERN.test(segment)
        ? segment
        : '_';
    }
    return NESTED_STATIC_ROUTES.has(segment) ? segment : '_';
  });
  return {
    path: `/${kept.join('/')}`,
    ...(workspaceSlug ? { workspaceSlug } : {}),
    linkable: !kept.includes('_'),
  };
}
